import { useQuery } from '@tanstack/react-query';
import { apiClient } from 'api';
import { useToast } from 'hooks/useToast';
import { useEffect, useRef } from 'react';

export interface CVAnalysisCandidate {
  id: number;
  name: string;
  email: string;
  key_strengths: string[];
  risks_gaps: string[];
  fit_score: string;
  recommendation: string;
  assessment?: number;
}

interface CVAnalysisCandidatesResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: CVAnalysisCandidate[];
}

interface UseCVAnalysisCandidatesParams {
  cvAnalysisId: string;
  sort_by?: string[];
}

/**
 * Hook for fetching CV analysis candidates with sorting and pagination
 */
export const useCVAnalysisCandidates = ({
  cvAnalysisId,
  sort_by = ['fit_score', 'name'],
}: UseCVAnalysisCandidatesParams) => {
  const { openToast } = useToast();

  const sortByStr = Array.isArray(sort_by) ? sort_by.join(',') : (sort_by as any) || '';

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['cvAnalysisCandidates', cvAnalysisId, sortByStr],
    queryFn: async () => {
      // This endpoint is server-paginated (DRF count/next/previous/results). The page lists,
      // selects, and paginates candidates client-side, so it needs the full set — not just the
      // server's first page. Walk every page and accumulate. A CV-analysis batch holds a bounded
      // number of candidates (the uploaded CVs), so this is at most a few requests.
      const PAGE_SIZE = 100;
      const MAX_PAGES = 100; // safety bound against an unexpected count/next loop
      const allResults: CVAnalysisCandidate[] = [];
      let page = 1;
      let totalCount = 0;

      while (page <= MAX_PAGES) {
        const params: Record<string, string | number> = {
          page,
          page_size: PAGE_SIZE,
        };
        if (sortByStr) {
          params.sort_by = sortByStr;
        }

        const response = await apiClient.get<CVAnalysisCandidatesResponse>(
          `/candidate/cv-analysis/${cvAnalysisId}/candidates/`,
          { params }
        );

        totalCount = response.data.count;
        allResults.push(...(response.data.results || []));

        if (!response.data.next || response.data.results.length === 0 || allResults.length >= totalCount) {
          break;
        }
        page += 1;
      }

      const fullData: CVAnalysisCandidatesResponse = {
        count: totalCount,
        next: null,
        previous: null,
        results: allResults,
      };
      return fullData;
    },
    retry: 1,
    enabled: !!cvAnalysisId,
  });

  const openToastRef = useRef(openToast);
  useEffect(() => {
    openToastRef.current = openToast;
  }, [openToast]);

  useEffect(() => {
    if (error) {
      const errorMessage = (error as any).response?.data?.message || (error as any).message || 'Failed to fetch candidates';
      openToastRef.current({
        show: true,
        heading: 'Error',
        text: errorMessage,
        type: 'error',
        timeOut: 5000,
      });
    }
  }, [error]);

  return {
    candidatesData: data,
    candidates: data?.results || [],
    totalCount: data?.count || 0,
    totalPages:
      data && Array.isArray(data.results) && data.results.length > 0
        ? Math.ceil((data.count || 0) / data.results.length)
        : 0,
    nextPage: data?.next,
    previousPage: data?.previous,
    isLoading,
    error,
    refetch,
  };
};